import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, BookOpen, Plus, Save, Loader2, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { Input } from '@/components/ui/input';
import { useSupabaseData } from '@/contexts/SupabaseDataContext';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const StudentEvolution = ({ onNavigate, student }) => {
  const { toast } = useToast();
  const { addClassLog, fetchClassLogsByStudent, deleteClassLog } = useSupabaseData();
  const [evolutionLogs, setEvolutionLogs] = useState([]);
  const [newLog, setNewLog] = useState('');
  const [instructor, setInstructor] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  const loadLogs = async () => {
    if (student) {
      setPageLoading(true);
      const logs = await fetchClassLogsByStudent(student.id);
      setEvolutionLogs(logs);
      setPageLoading(false);
    }
  };

  useEffect(() => {
    loadLogs();
  }, [student]);

  const handleSaveLog = async (e) => {
    e.preventDefault();
    if (!newLog.trim() || !instructor.trim()) {
      toast({
        title: "Erro",
        description: "Por favor, preencha a data, o nome do instrutor e o relato da aula.",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    const logEntry = {
      student_id: student.id,
      date,
      instructor,
      report: newLog,
      behavior: 'Não especificado' // Placeholder, can be expanded later
    };

    const newEntry = await addClassLog(logEntry);
    setLoading(false);

    if (newEntry) {
      setEvolutionLogs(prev => [newEntry, ...prev]);
      setNewLog('');
      setInstructor('');
      setDate(new Date().toISOString().split('T')[0]);
    }
  };

  const handleDeleteLog = async (id) => {
    const success = await deleteClassLog(id);
    if (success) {
      setEvolutionLogs(prev => prev.filter(log => log.id !== id));
    }
  };

  if (!student) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-white text-xl">Selecione um aluno para ver a evolução.</p>
        <Button onClick={() => onNavigate('dashboard')} className="ml-4">Voltar</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Button
            onClick={() => onNavigate('studentDashboard', student)}
            variant="ghost"
            className="text-white hover:bg-white/10 mb-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar para {student.name}
          </Button>
          
          <h1 className="text-4xl font-bold text-white mb-2">
            Evolução de {student.name}
          </h1>
          <p className="text-xl text-purple-300">
            Relatos sobre as aulas e comportamento do aluno.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8"
        >
          <Card className="bg-transparent border-white/20">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <Plus className="mr-2 h-5 w-5" />
                Adicionar Novo Relato
              </CardTitle>
            </CardHeader>
            <form onSubmit={handleSaveLog}>
              <CardContent className="space-y-4">
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                     <div className="space-y-2">
                        <Label htmlFor="date" className="text-white">Data</Label>
                        <Input
                            id="date"
                            type="date"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                             className="bg-white/10 border-white/20 text-white"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="instructor" className="text-white">Instrutor</Label>
                        <Input
                            id="instructor"
                            type="text"
                            value={instructor}
                            onChange={(e) => setInstructor(e.target.value)}
                             className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                            placeholder="Seu nome"
                        />
                    </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-log" className="text-white">
                    Relato da aula e comportamento do aluno
                  </Label>
                  <Textarea
                    id="new-log"
                    value={newLog}
                    onChange={(e) => setNewLog(e.target.value)}
                    placeholder="Descreva aqui o que foi feito na aula e como o aluno se comportou..."
                    rows={5}
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                  />
                </div>
                 <div className="flex justify-end">
                  <Button
                    type="submit"
                    disabled={loading}
                    className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white"
                  >
                    {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                    {loading ? 'Salvando...' : 'Salvar Relato'}
                  </Button>
                </div>
              </CardContent>
            </form>
          </Card>
        </motion.div>

        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
        >
          <Card className="bg-transparent border-white/20">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <BookOpen className="mr-2 h-5 w-5" />
                Histórico de Evolução
              </CardTitle>
            </CardHeader>
            <CardContent>
              {pageLoading ? (
                <div className="flex justify-center items-center py-8">
                  <Loader2 className="h-8 w-8 text-white animate-spin" />
                </div>
              ) : evolutionLogs.length > 0 ? (
                <div className="space-y-4">
                  {evolutionLogs.map(log => (
                    <div key={log.id} className="p-4 rounded-lg bg-white/5 border border-white/10 group">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-semibold text-white">
                            {new Date(log.date).toLocaleDateString('pt-BR', {timeZone: 'UTC'})}
                          </p>
                          <p className="text-sm text-white/70">Instrutor: {log.instructor}</p>
                        </div>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Excluir Relato?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Esta ação é permanente. Tem certeza que deseja excluir este relato de evolução?
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDeleteLog(log.id)}>Excluir</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                      <p className="text-white/90 mt-1">{log.report}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-white/60 py-8">
                  Nenhum relato de evolução encontrado para este aluno.
                </p>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default StudentEvolution;